import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { getLesson, listLessons } from "../../graphql/queries"; // Adjusted import path
import { updateUserLesson } from "../../graphql/mutations"; // Adjusted import path
import { withTimeout } from "../../utils/userLessonUtils";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Fade,
  Slide,
  Zoom,
  Paper,
} from "@mui/material";
import { useLessonContext } from "../../context/LessonContext";
import { useUserContext } from "../../context/UserContext";
import SchoolIcon from "@mui/icons-material/School";


import "./Lesson.css";

const Lesson = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    state: lessonState,
    dispatch: lessonDispatch,
    findOrCreateUserLesson,
    refreshUserLessons,
  } = useLessonContext();
  const { state: userState } = useUserContext();
  const {
    lesson,
    userLesson,
    nextLessonID,
    previousLessonID,
    lessonLoading,
    userLessonLoading,
    lessonError,
    userLessonError,
  } = lessonState;
  const { userAttributes } = userState;
  const interactionHandledRef = useRef(false);
  const userLessonCreatedRef = useRef(false);
  const abortControllerRef = useRef(null);
  const client = useMemo(() => generateClient(), []);
  const [isNextButtonEnabled, setIsNextButtonEnabled] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [showCompletionBanner, setShowCompletionBanner] = useState(false);
  const iframeRef = useRef(null);
  const autoRedirectTimeoutRef = useRef(null);

  // Check if this is the last lesson
  const isLastLesson = !nextLessonID;

  // Helper function to extract user information from attributes
  const getUserInfo = useCallback(() => {
    if (!userAttributes) return {};
    
    return {
      firstName: userAttributes.given_name || userAttributes.name?.split(' ')[0] || '',
      lastName: userAttributes.family_name || userAttributes.name?.split(' ').slice(1).join(' ') || '',
      email: userAttributes.email || '',
      dateOfBirth: userAttributes.birthdate || null
    };
  }, [userAttributes]);

  const fetchLessonData = useCallback(async () => {
    try {
      const lessonResult = await withTimeout(
        client.graphql({ query: getLesson, variables: { id } }),
        30000,
        `getLesson ${id}`
      );

      const currentLesson = lessonResult.data.getLesson;
      lessonDispatch({ type: 'SET_LESSON', payload: currentLesson });

      const nextLessonResult = await withTimeout(
        client.graphql({
          query: listLessons,
          variables: { filter: { order: { gt: currentLesson.order } } },
        }),
        30000,
        'listLessons next'
      );
      let nextLesson = nextLessonResult.data.listLessons.items;
      nextLesson = nextLesson.sort((a, b) => a.order - b.order)[0];
      lessonDispatch({ type: 'SET_NEXT_LESSON_ID', payload: nextLesson?.id ?? null });

      const previousLessonResult = await withTimeout(
        client.graphql({
          query: listLessons,
          variables: { filter: { order: { lt: currentLesson.order } } },
        }),
        30000,
        'listLessons previous'
      );
      let previousLesson = previousLessonResult.data.listLessons.items;
      previousLesson = previousLesson.sort((a, b) => b.order - a.order)[0];
      lessonDispatch({ type: 'SET_PREVIOUS_LESSON_ID', payload: previousLesson?.id ?? null });
    } catch (error) {
      lessonDispatch({ type: 'SET_LESSON_ERROR', payload: true });
    }
  }, [id, client, lessonDispatch]);

  const fetchUserLessonsData = useCallback(async () => {
    try {
      const userID = userAttributes?.sub;
      if (!userID) return;

      console.log(`fetchUserLessonsData: starting for user ${userID}, lesson ${id}`);

      // Only create if we haven't tried creating yet
      if (!userLessonCreatedRef.current) {
        console.log(`fetchUserLessonsData: attempting cache-aware creation for ${userID}:${id}`);
        userLessonCreatedRef.current = true; // Set this early to prevent concurrent attempts
        
        try {
          const userInfo = getUserInfo();
          const createPayload = () => ({
            userID,
            lessonID: id,
            progress: 0,
            startedAt: new Date().toISOString(),
            ...userInfo
          });
          
          // Use the cache-aware idempotent creation function
          // This will check cache → targeted lookup → create only if needed
          const userLessonRecord = await findOrCreateUserLesson(userID, id, createPayload);
          
          console.log(`fetchUserLessonsData: got userLesson record ${userLessonRecord.id} (progress ${userLessonRecord.progress}%)`);
          
          lessonDispatch({ type: 'SET_USER_LESSON', payload: userLessonRecord });
          lessonDispatch({ type: 'ADD_USER_LESSON', payload: userLessonRecord });
          
        } catch (createError) {
          console.error('Error in findOrCreateUserLesson:', createError);
          // Reset the flag so we can try again if needed
          userLessonCreatedRef.current = false;
          throw createError;
        }
      }
    } catch (error) {
      console.error('Error fetching user lesson data:', error);
      lessonDispatch({ type: 'SET_USER_LESSON_ERROR', payload: true });
      // Reset the creation flag on error
      userLessonCreatedRef.current = false;
    }
  }, [id, userAttributes, lessonDispatch, getUserInfo, findOrCreateUserLesson]);

  // Reset refs and state when lesson ID changes
  useEffect(() => {
    // Only run this effect when we have an ID
    if (!id) return;
    
    // Abort any pending requests for the previous lesson
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    // Reset refs when navigating to a new lesson
    interactionHandledRef.current = false;
    userLessonCreatedRef.current = false;
    setIsNextButtonEnabled(false);
    setIsContentVisible(false); // Hide content during transition
    setShowCompletionBanner(false); // Reset completion banner
    
    // Clear any existing auto-redirect timeout
    if (autoRedirectTimeoutRef.current) {
      clearTimeout(autoRedirectTimeoutRef.current);
      autoRedirectTimeoutRef.current = null;
    }
    
    // Clear iframe ref to ensure clean slate for new lesson
    if (iframeRef.current) {
      iframeRef.current = null;
    }
    
    // Scroll to top when lesson changes
    window.scrollTo({ top: 0, behavior: 'auto' });
    
    // Reset loading states and clear previous lesson data to prevent stale state
    lessonDispatch({ type: 'SET_LESSON_LOADING', payload: true });
    lessonDispatch({ type: 'SET_USER_LESSON_LOADING', payload: true });
    lessonDispatch({ type: 'SET_LESSON', payload: null });
    lessonDispatch({ type: 'SET_USER_LESSON', payload: null });
    
    // Cleanup function to abort on unmount or ID change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [id, lessonDispatch]);

  // Show content with transition when lesson and userLesson are ready
  useEffect(() => {
    if (lesson && userLesson && !lessonLoading && !userLessonLoading) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setIsContentVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [lesson, userLesson, lessonLoading, userLessonLoading]);

  // Show completion banner if user loads the last lesson and it's already completed
  useEffect(() => {
    if (isLastLesson && userLesson && userLesson.progress >= 100 && isContentVisible) {
      setShowCompletionBanner(true);
    }
  }, [isLastLesson, userLesson, isContentVisible]);

  useEffect(() => {
    if (id && !lessonError) {
      fetchLessonData();
    }
  }, [fetchLessonData, lessonError, id]);

  useEffect(() => {
    if (id && !userLessonError && userAttributes) {
      fetchUserLessonsData();
    }
  }, [fetchUserLessonsData, userLessonError, userAttributes, id]);

  // Function to mark survey as completed and update progress
  const markSurveyAsCompleted = useCallback(async () => {
    console.log('markSurveyAsCompleted called', { userLesson, progress: userLesson?.progress });
    
    if (!userLesson) {
      console.log('No userLesson found, cannot mark as completed');
      return;
    }
    
    if (userLesson.progress >= 100) {
      console.log('Survey already completed, enabling next button');
      setIsNextButtonEnabled(true);
      return;
    }
    
    try {
      console.log('Updating user lesson progress to 100%');
      const updatedUserLesson = await client.graphql({
        query: updateUserLesson,
        variables: {
          input: {
            id: userLesson.id,
            progress: 100,
            finishedAt: new Date().toISOString(),
          },
        },
      });
      
      console.log('Successfully updated user lesson:', updatedUserLesson.data.updateUserLesson);
      
      lessonDispatch({ type: 'SET_USER_LESSON', payload: updatedUserLesson.data.updateUserLesson });
      lessonDispatch({ type: 'UPDATE_USER_LESSON', payload: updatedUserLesson.data.updateUserLesson });
      lessonDispatch({ type: 'SET_IS_UPDATING_USER_LESSONS', payload: true });
      setIsNextButtonEnabled(true);
      
      console.log('Survey completion successfully processed');
    } catch (error) {
      console.error('Error updating user lesson progress:', error);
      // Still enable the button even if the update fails
      setIsNextButtonEnabled(true);
    }
  }, [userLesson, client, lessonDispatch]);

  // Effect to handle button state based on lesson progress and survey completion
  useEffect(() => {
    if (!lesson || !userLesson) {
      setIsNextButtonEnabled(false);
      return;
    }

    // If lesson is already completed (progress 100), enable next button immediately
    if (userLesson.progress >= 100) {
      setIsNextButtonEnabled(true);
      return;
    }

    // If no survey or survey is empty, enable next button immediately
    if (!lesson.survey || lesson.survey.trim() === '') {
      setIsNextButtonEnabled(true);
      return;
    }

    // If survey exists but lesson is not completed, disable next button initially
    // The button will be enabled when survey is completed through markSurveyAsCompleted
    setIsNextButtonEnabled(false);
  }, [lesson, userLesson]);

  // Google Forms completion detection
  useEffect(() => {
    // Only set up survey detection if we have lesson data and it's not completed
    if (!lesson || !userLesson || userLesson.progress >= 100) {
      return;
    }
    
    if (!lesson.survey || !lesson.survey.includes('<iframe')) {
      return;
    }

    let iframe = null;
    let loadCount = 0;

    // Function to handle iframe load events
    const handleIframeLoad = () => {
      loadCount++;
      console.log(`Iframe load event ${loadCount}`);
      
      // Google Forms typically loads twice:
      // 1. Initial form load
      // 2. Thank you page after submission
      if (loadCount >= 2) {
        console.log('Survey completed - second load detected');
        markSurveyAsCompleted();
      }
    };

    // Function to handle postMessage events from Google Forms
    const handleMessage = (event) => {
      console.log('Received postMessage:', event.data, 'from origin:', event.origin);
      
      // Check for Google Forms origins
      if (!event.origin.includes('docs.google.com') && !event.origin.includes('google.com')) {
        return;
      }

      // Check for various completion indicators in the message data
      if (event.data) {
        const dataStr = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
        
        // Google Forms completion patterns
        if (dataStr.includes('formResponse') || 
            dataStr.includes('submitted') ||
            dataStr.includes('submit') ||
            dataStr.includes('complete') ||
            dataStr.includes('thanks') ||
            dataStr.includes('gracias') ||
            dataStr.includes('Puedes continuar') ||
            dataStr.includes('Thank you') ||
            dataStr.includes('success')) {
          console.log('Survey completion detected via postMessage');
          markSurveyAsCompleted();
        }
      }
    };

    // Function to set up iframe monitoring
    const setupIframeMonitoring = () => {
      const iframeContainer = iframeRef.current;
      if (iframeContainer) {
        iframe = iframeContainer.querySelector('iframe');
        if (iframe) {
          console.log('Iframe found, setting up load listener');
          iframe.addEventListener('load', handleIframeLoad);
          return true;
        }
      }
      return false;
    };

    // Try to set up iframe monitoring immediately
    if (!setupIframeMonitoring()) {
      // If iframe not found, wait for it with MutationObserver
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            if (setupIframeMonitoring()) {
              observer.disconnect();
            }
          }
        });
      });

      const iframeContainer = iframeRef.current;
      if (iframeContainer) {
        observer.observe(iframeContainer, { childList: true, subtree: true });
        
        // Disconnect observer after 10 seconds
        setTimeout(() => {
          observer.disconnect();
        }, 10000);
      }
    }

    // Set up postMessage listener
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      if (iframe) {
        iframe.removeEventListener('load', handleIframeLoad);
      }
    };
  }, [lesson, userLesson, markSurveyAsCompleted]);

  // Cleanup timeout when component unmounts
  useEffect(() => {
    return () => {
      if (autoRedirectTimeoutRef.current) {
        clearTimeout(autoRedirectTimeoutRef.current);
      }
    };
  }, []);

  const handleInteraction = async () => {
    if (
      userLesson &&
      userLesson.progress < 50 &&
      !interactionHandledRef.current
    ) {
      try {
        const updatedUserLesson = await client.graphql({
          query: updateUserLesson,
          variables: {
            input: {
              id: userLesson.id,  
              progress: 50,
            },
          },
        });
        lessonDispatch({ type: 'SET_USER_LESSON', payload: updatedUserLesson.data.updateUserLesson });
        lessonDispatch({ type: 'UPDATE_USER_LESSON', payload: updatedUserLesson.data.updateUserLesson });
        lessonDispatch({ type: 'SET_IS_UPDATING_USER_LESSONS', payload: true });
        interactionHandledRef.current = true;
      } catch (error) {
        // Error updating user lesson progress
      }
    }
  };

  const completeCurrentLessonAndNavigateNext = async () => {
    if (!userLesson || !isNextButtonEnabled) {
      return;
    }

    try {
      // Only update progress if it's not already 100
      if (userLesson.progress < 100) {
        console.log('Marking lesson as complete (progress 100%)');
        const updatedUserLesson = await client.graphql({
          query: updateUserLesson,
          variables: {
            input: {
              id: userLesson.id,
              progress: 100,
              finishedAt: new Date().toISOString(),
            },
          },
        });
        
        // Update both the current user lesson and the list
        lessonDispatch({ type: 'SET_USER_LESSON', payload: updatedUserLesson.data.updateUserLesson });
        lessonDispatch({ type: 'UPDATE_USER_LESSON', payload: updatedUserLesson.data.updateUserLesson });
        lessonDispatch({ type: 'SET_IS_UPDATING_USER_LESSONS', payload: true });
        
        console.log('Lesson marked as complete:', updatedUserLesson.data.updateUserLesson);
      }
      
      // If this is the last lesson, show completion banner and set up auto-redirect
      if (isLastLesson) {
        setShowCompletionBanner(true);
        // Clear any existing timeout and set up auto-redirect
        if (autoRedirectTimeoutRef.current) {
          clearTimeout(autoRedirectTimeoutRef.current);
        }
        autoRedirectTimeoutRef.current = setTimeout(() => {
          navigate('/');
        }, 4000);
      } else {
        // Navigate to next lesson
        navigate(`/lecciones/lesson/${nextLessonID}`);
      }
    } catch (error) {
      console.error('Error updating user lesson progress:', error);
      // If this is the last lesson, still show completion behavior
      if (isLastLesson) {
        setShowCompletionBanner(true);
        // Clear any existing timeout and set up auto-redirect
        if (autoRedirectTimeoutRef.current) {
          clearTimeout(autoRedirectTimeoutRef.current);
        }
        autoRedirectTimeoutRef.current = setTimeout(() => {
          navigate('/');
        }, 4000);
      } else {
        // Still navigate even if update fails
        navigate(`/lecciones/lesson/${nextLessonID}`);
      }
    }
  };

  const navigateToPreviousLesson = () => {
    if (previousLessonID) {
      navigate(`/lecciones/lesson/${previousLessonID}`);
    }
  };

  const navigateToHome = () => {
    // Clear auto-redirect timeout since user is manually navigating
    if (autoRedirectTimeoutRef.current) {
      clearTimeout(autoRedirectTimeoutRef.current);
      autoRedirectTimeoutRef.current = null;
    }
    navigate('/');
  };

  // If no lesson ID is provided, show the lesson selection message
  if (!id) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%',
        p: 4,
        textAlign: 'center'
      }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, borderRadius: 3 }}>
          <SchoolIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
            ¡Bienvenido a las lecciones!
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Selecciona una lección de la lista de la izquierda para comenzar tu aprendizaje.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Check if survey should be displayed
  const shouldShowSurvey = userLesson && lesson && userLesson.progress < 100 && lesson.survey && lesson.survey.trim() !== '';

  if (lessonLoading || userLessonLoading || !lesson || !userLesson) {
    return (
      <Box className="lesson-container">
        <Box className="lesson-loading-center">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary', fontWeight: 400 }}>
            Cargando lección...
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.disabled' }}>
            Un momento por favor
          </Typography>
        </Box>
      </Box>
    );
  }

  if (lessonError || userLessonError) {
    const handleRetry = () => {
      if (lessonError) {
        lessonDispatch({ type: 'SET_LESSON_ERROR', payload: false });
        lessonDispatch({ type: 'SET_LESSON_LOADING', payload: true });
      }
      if (userLessonError) {
        userLessonCreatedRef.current = false;
        if (userAttributes?.sub) {
          refreshUserLessons(userAttributes.sub);
        }
        lessonDispatch({ type: 'SET_USER_LESSON_ERROR', payload: false });
        lessonDispatch({ type: 'SET_USER_LESSON_LOADING', payload: true });
      }
    };

    return (
      <Box className="lesson-container">
        <Box className="lesson-loading-center" sx={{ gap: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Alert severity="error" sx={{ maxWidth: 400 }}>
            Error loading lesson data. Please try again later.
          </Alert>
          <Button variant="outlined" color="primary" onClick={handleRetry}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Fade in={isContentVisible} timeout={600}>
        <Box className="lesson-content" onClick={handleInteraction}>
          <Slide direction="up" in={isContentVisible} timeout={500}>
            <Typography variant="h4" gutterBottom>
              {lesson?.title || 'Loading...'}
            </Typography>
          </Slide>

          {/* Course completion banner */}
          {showCompletionBanner && (
            <Zoom in={showCompletionBanner} timeout={600} style={{ transitionDelay: '200ms' }}>
              <Box sx={{
                p: 3,
                mb: 3,
                backgroundColor: 'success.light',
                borderRadius: 2,
                border: 1,
                borderColor: 'success.main',
                textAlign: 'center'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.dark', mb: 1 }}>
                  ¡Has terminado por ahora!
                </Typography>
                <Typography variant="body1" sx={{ color: 'success.dark', mb: 2 }}>
                  No hay más lecciones disponibles en este momento. Vuelve más tarde para ver nuevas lecciones.
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  onClick={navigateToHome}
                  sx={{ fontWeight: 600 }}
                >
                  Ir al inicio
                </Button>
              </Box>
            </Zoom>
          )}
          
          {/* Displays lesson's content */}
          {lesson?.content && (
            <Fade in={isContentVisible} timeout={800}>
              <Box dangerouslySetInnerHTML={{ __html: lesson.content }} />
            </Fade>
          )}
          
          {/* Show completion message when survey was already answered */}
          {userLesson && userLesson.progress >= 100 && lesson?.survey && lesson.survey.trim() !== '' && (
            <Zoom in={isContentVisible} timeout={600} style={{ transitionDelay: '200ms' }}>
              <Box className="lesson-survey-completed">
                <Typography variant="body1" className="message-text">
                  <span className="message-icon">🎉</span>
                  ¡Excelente! Ya has completado la encuesta de esta lección.
                </Typography>
              </Box>
            </Zoom>
          )}
          
          {/* Conditionally render survey only if lesson is not completed */}
          {shouldShowSurvey && (
            <Fade in={isContentVisible} timeout={1000} style={{ transitionDelay: '400ms' }}>
              <Box className="lesson-loading">
                {lesson.survey.includes('<iframe') ? (
                  <>
                    <div
                      key={`survey-${lesson.id}-${userLesson.id}`} // Add key to force re-render
                      ref={iframeRef}
                      dangerouslySetInnerHTML={{ __html: lesson.survey }}
                    />
                    {/* Survey completion status */}
                    {isNextButtonEnabled && userLesson && userLesson.progress < 100 && (
                      <Zoom in={true} timeout={600} style={{ transitionDelay: '600ms' }}>
                        <Box className="lesson-completed">
                          <Typography variant="body1" className="message-text">
                            <span className="message-icon">✅</span>
                            ¡Encuesta completada! Puedes continuar a la siguiente lección.
                          </Typography>
                        </Box>
                      </Zoom>
                    )}
                    {/* Show waiting message when survey is not completed */}
                    {!isNextButtonEnabled && (
                      <Zoom in={true} timeout={600} style={{ transitionDelay: '600ms' }}>
                        <Box className="lesson-next-available">
                          <Typography variant="body1" className="message-text">
                            <span className="message-icon">📋</span>
                            Por favor, completa la encuesta para continuar.
                          </Typography>
                        </Box>
                      </Zoom>
                    )}
                  </>
                ) : (
                  <Box 
                    key={`survey-${lesson.id}-${userLesson.id}`} // Add key to force re-render
                    dangerouslySetInnerHTML={{ __html: lesson.survey }} 
                  />
                )}
              </Box>
            </Fade>
          )}
        </Box>
      </Fade>

      {/* Navigation buttons contained within lesson content area */}
      <Slide direction="up" in={isContentVisible} timeout={700} style={{ transitionDelay: '300ms' }}>
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          mt: 4,
          pt: 3,
          borderTop: 1,
          borderColor: 'divider'
        }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={navigateToPreviousLesson}
            disabled={!previousLessonID}
          >
            Previous
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={completeCurrentLessonAndNavigateNext}
            disabled={(!isLastLesson && (!nextLessonID || !isNextButtonEnabled)) || (isLastLesson && !isNextButtonEnabled)}
          >
            {isLastLesson ? 'Finalizar' : 'Siguiente'}
          </Button>
        </Box>
      </Slide>
    </Box>
  );
};

export default Lesson;
